// components/esg-metrics/templates/columns.ts
import type { ColumnDef } from '@tanstack/vue-table'
import { Button } from '~/components/ui/button'
import AlphabeticSortIcon from '~/components/AlphabeticSortIcon.vue'
import DataTableFilterableHeader from '~/components/ui/table/DataTableFilterableHeader.vue'
import UserInitialsTableCell from '~/components/UserInitialsTableCell.vue'
import PenIcon from '~/components/PenIcon.vue'
import TrashGreenIcon from '~/components/TrashGreenIcon.vue'
import ExpandIcon from '~/components/ExpandIcon.vue'

export const createTemplateColumns = (
  filterOptions: EsgTemplateFilterOptions,
  showMetricsModal: (metrics: EsgMetricItem[]) => void,
): ColumnDef<EsgTemplateWithRelations>[] => [
  {
    accessorKey: 'name',
    id: 'name',
    header: ({ column }) => {
      const isSorted = column.getIsSorted()
      const sortIconStyling = isSorted === 'asc' ? 'text-green-500' : isSorted === 'desc' ? 'text-red-500 rotate-180' : ''

      return h('div', { class: 'flex justify-between items-center' }, [
        h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'Template'),
        h(Button, {
          class: 'text-main-primary',
          variant: 'ghost',
          onClick: () => column.toggleSorting(isSorted === 'asc', true),
        }, () => h(AlphabeticSortIcon, { customStyle: `size-5 ${sortIconStyling}` })),
      ])
    },
    cell: ({ row }) => h('div', { class: 'font-medium no-localize-translate' }, row.getValue('name')),
  },

  // Metrics count column
  {
    accessorKey: 'metrics',
    id: 'metrics',
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'Mapped Metrics',
      filterData: filterOptions.metrics.map(metric => ({ id: metric.id, name: metric.name })),
    }),
    cell: ({ row }) => {
      const metrics = row.getValue('metrics') as EsgMetricItem[]
      return h('div', { class: 'flex justify-between items-center' }, [
        h('span', { class: 'capitalize text-left text-md text-foreground' }, `${metrics.length} Metrics`),
        h('button', {
          class: 'p-4 hover:bg-slate-200 rounded-md',
          onClick: () => {
            showMetricsModal(metrics)
          },
        }, [
          h(ExpandIcon, { class: 'h-4 w-4' }),
        ]),
      ])
    },
  },

  // Data owners column
  {
    accessorKey: 'data_owners',
    id: 'data_owner',
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'Data Owner',
      filterData: filterOptions.users.map(user => ({ id: user.id, name: user.name })),
    }),
    cell: ({ row, column }) => h(UserInitialsTableCell, { users: row.getValue(column.id) as { id: number, name: string }[] }),
  },

  // Data approvers column
  {
    accessorKey: 'data_approvers',
    id: 'data_approver',
    header: ({ column }) => h(DataTableFilterableHeader, {
      column,
      title: 'Data Approver',
      filterData: filterOptions.users.map(user => ({ id: user.id, name: user.name })),
    }),
    cell: ({ row, column }) => h(UserInitialsTableCell, { users: row.getValue(column.id) as { id: number, name: string }[] }),
  },

  // Actions column
  {
    id: 'actions',
    size: 50,
    header: () => h('span', { class: 'text-sm text-slate-800 font-semibold capitalize' }, 'Actions'),
    cell: ({ row }) => {
      const templateId = row.original.id
      const { editTemplate, deleteTemplate } = inject('templateActions') as {
        editTemplate: (templateId: string) => void
        deleteTemplate: (templateId: string) => void
      }

      return h('div', { class: 'flex items-center' }, [
        h(Button, {
          class: 'text-main-primary',
          variant: 'link',
          size: 'sm',
          onClick: (e: Event) => {
            e.stopPropagation()
            editTemplate(templateId)
          },
        }, () => h(PenIcon)),
        h(Button, {
          class: 'text-red-500',
          variant: 'link',
          size: 'sm',
          onClick: (e: Event) => {
            e.stopPropagation()
            deleteTemplate(templateId)
          },
        }, () => h(TrashGreenIcon)),
      ])
    },
  },
]
